import { c as _c } from "react/compiler-runtime";
type CommonBorderedRadioButtonProps<T extends string> = {
  readonly type: T;
  readonly selected: T;
  readonly disabled?: boolean | undefined;
  readonly variant?: "default" | "subtle" | undefined;
  readonly onChange: (type: T) => void;
} & ({
  readonly label: string;
  readonly children?: undefined;
} | {
  readonly label?: undefined;
  readonly children: React.ReactNode;
});
function getWrapperClasses({
  disabled,
  isSelected,
  isSubtle
}: {
  readonly disabled: boolean;
  readonly isSelected: boolean;
  readonly isSubtle: boolean;
}) {
  if (isSelected && isSubtle) {
    return "tw-rounded-xl tw-border-primary-400 tw-bg-primary-500/5 tw-ring-primary-500/30 tw-shadow-inner";
  }
  if (isSelected) {
    return "tw-rounded-lg tw-border-transparent tw-bg-primary-500/20 tw-ring-primary-400 tw-shadow-xl";
  }
  if (isSubtle) {
    const hoverClasses = disabled ? "" : "hover:tw-border-white/10 hover:tw-bg-iron-800 hover:tw-ring-white/10";
    return `tw-rounded-xl tw-border-white/5 tw-bg-iron-900 tw-ring-white/5 tw-shadow-inner ${hoverClasses}`;
  }
  const hoverClasses = disabled ? "" : "hover:tw-ring-iron-650";
  return `tw-rounded-lg tw-border-transparent tw-ring-iron-700 tw-bg-iron-800 tw-shadow ${hoverClasses}`;
}
function getLabelClasses({
  disabled,
  isSelected,
  isSubtle
}: {
  readonly disabled: boolean;
  readonly isSelected: boolean;
  readonly isSubtle: boolean;
}) {
  if (isSelected) {
    return "tw-text-primary-400";
  }
  if (isSubtle) {
    const hoverClasses = disabled ? "" : "group-hover:tw-text-iron-300";
    return `tw-text-iron-400 ${hoverClasses}`;
  }
  const hoverClasses = disabled ? "" : "group-hover:tw-text-iron-200";
  return `tw-text-iron-300 ${hoverClasses}`;
}
export default function CommonBorderedRadioButton(t0) {
  const $ = _c(34);
  const {
    type,
    selected,
    label,
    disabled: t1,
    variant: t2,
    onChange,
    children
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const isSelected = selected === type;
  const isSubtle = variant === "subtle";
  let t3;
  if ($[0] !== disabled || $[1] !== isSelected || $[2] !== isSubtle) {
    t3 = getWrapperClasses({
      disabled,
      isSelected,
      isSubtle
    });
    $[0] = disabled;
    $[1] = isSelected;
    $[2] = isSubtle;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const wrapperClasses = t3;
  const inputClasses = isSelected ? "tw-text-primary-500 focus:tw-ring-primary-500" : "tw-text-primary-400 focus:tw-ring-primary-400";
  let t4;
  if ($[4] !== disabled || $[5] !== isSelected || $[6] !== isSubtle) {
    t4 = getLabelClasses({
      disabled,
      isSelected,
      isSubtle
    });
    $[4] = disabled;
    $[5] = isSelected;
    $[6] = isSubtle;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const labelClasses = t4;
  let t5;
  if ($[8] !== disabled || $[9] !== onChange || $[10] !== type) {
    t5 = () => {
      if (!disabled) {
        onChange(type);
      }
    };
    $[8] = disabled;
    $[9] = onChange;
    $[10] = type;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const onSelectedChange = t5;
  const t6 = `${wrapperClasses} tw-group tw-relative tw-flex tw-flex-1 ${isSubtle ? "tw-items-start" : "tw-items-center"} tw-gap-x-3 tw-border tw-border-solid tw-px-4 tw-py-4 tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out focus:tw-outline-none ${disabled ? "tw-cursor-not-allowed tw-opacity-50" : "tw-cursor-pointer"}`;
  const t7 = isSubtle ? "tw-peer tw-sr-only" : `${inputClasses} tw-form-radio tw-h-4 tw-w-4 tw-cursor-pointer tw-border tw-border-solid tw-border-iron-650 tw-bg-iron-800 tw-ring-offset-iron-800 tw-transition tw-duration-300 tw-ease-out focus:tw-ring-2 sm:tw-h-5 sm:tw-w-5`;
  let t8;
  if ($[12] !== disabled || $[13] !== isSelected || $[14] !== onSelectedChange || $[15] !== t7 || $[16] !== type) {
    t8 = <input id={type} type="radio" disabled={disabled} checked={isSelected} onChange={onSelectedChange} className={t7} />;
    $[12] = disabled;
    $[13] = isSelected;
    $[14] = onSelectedChange;
    $[15] = t7;
    $[16] = type;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== isSelected || $[19] !== isSubtle) {
    t9 = isSubtle && <span aria-hidden="true" className={`tw-mt-1 tw-flex tw-h-4 tw-w-4 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition tw-duration-300 tw-ease-out peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-primary-500 peer-focus-visible:tw-ring-offset-2 peer-focus-visible:tw-ring-offset-iron-950 ${isSelected ? "tw-border-primary-400 tw-bg-primary-500/10" : "tw-border-iron-600 tw-bg-transparent group-hover:tw-border-iron-500"}`}><span className={`tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-400 tw-transition tw-duration-200 ${isSelected ? "tw-scale-100" : "tw-scale-0"}`} /></span>;
    $[18] = isSelected;
    $[19] = isSubtle;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== children || $[22] !== disabled || $[23] !== label || $[24] !== labelClasses) {
    t10 = label ? <span className={`${labelClasses} ${disabled ? "tw-text-iron-600" : ""}`}>{label}</span> : children;
    $[21] = children;
    $[22] = disabled;
    $[23] = label;
    $[24] = labelClasses;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <div className="tw-flex tw-items-center tw-truncate tw-whitespace-nowrap tw-transition tw-duration-300 tw-ease-out"><div className="tw-flex tw-flex-col tw-truncate tw-text-base tw-font-semibold tw-transition tw-duration-300 tw-ease-out">{t10}</div></div>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== onSelectedChange || $[29] !== t11 || $[30] !== t6 || $[31] !== t8 || $[32] !== t9) {
    t12 = <div onClick={onSelectedChange} className={t6}>{t8}{t9}{t11}</div>;
    $[28] = onSelectedChange;
    $[29] = t11;
    $[30] = t6;
    $[31] = t8;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}